import { useState, useEffect, useRef } from 'react'
import { api } from '../auth/api'

const SOURCE_BADGE = { web: '🌐', telegram: '📱', system: '⚙️' }

function formatTime(ts) {
    if (!ts) return ''
    return new Date(ts).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function MessageBubble({ msg }) {
    const role = msg.role
    if (role === 'system') {
        return (
            <div style={{ textAlign: 'center', margin: '12px 0' }}>
                <span style={{
                    fontStyle: 'italic', fontSize: '12px', color: '#64748b',
                    background: '#1e2a3a', borderRadius: '12px', padding: '4px 12px',
                    display: 'inline-block'
                }}>
                    {SOURCE_BADGE[msg.source] || '⚙️'} {msg.content}
                </span>
                <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>{formatTime(msg.created_at)}</div>
            </div>
        )
    }

    const isUser = role === 'user'
    return (
        <div style={{
            display: 'flex', flexDirection: 'column',
            alignItems: isUser ? 'flex-end' : 'flex-start',
            marginBottom: '12px',
        }}>
            <div style={{
                maxWidth: '75%',
                background: isUser ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.06)',
                border: isUser ? '1px solid rgba(59,130,246,0.3)' : '1px solid rgba(255,255,255,0.1)',
                borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                padding: '10px 14px',
            }}>
                <div style={{ fontSize: '13px', color: '#ccd6f6', lineHeight: 1.5 }}>{msg.content}</div>
            </div>
            <div style={{ fontSize: '11px', color: '#475569', marginTop: '3px', display: 'flex', gap: '6px' }}>
                {isUser ? (
                    <span>{msg.user_email || 'You'}</span>
                ) : (
                    <span style={{ color: '#D4AF37', fontWeight: 'bold' }}>LESIA</span>
                )}
                <span>{SOURCE_BADGE[msg.source] || ''}</span>
                <span>{formatTime(msg.created_at)}</span>
            </div>
        </div>
    )
}

export default function DebateChatPanel({ caseId, onBriefRefresh }) {
    const [thread, setThread] = useState([])
    const [input, setInput] = useState('')
    const [sending, setSending] = useState(false)
    const [rerunStatus, setRerunStatus] = useState(null) // null | 'polling'
    const [showConfirm, setShowConfirm] = useState(false)
    const [error, setError] = useState('')
    const bottomRef = useRef(null)
    const pollRef = useRef(null)

    const loadThread = async () => {
        try {
            const res = await api.get(`/cases/${caseId}/thread`)
            setThread(res.data.thread || [])
        } catch (e) {
            console.error('Thread load failed:', e)
        }
    }

    useEffect(() => {
        loadThread()
        return () => { if (pollRef.current) clearInterval(pollRef.current) }
    }, [caseId])

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [thread])

    const sendReply = async () => {
        const msg = input.trim()
        if (!msg || sending) return
        setSending(true)
        setError('')
        try {
            await api.post(`/cases/${caseId}/reply`, { message: msg, mode: 'reply' })
            setInput('')
            await loadThread()
            if (onBriefRefresh) await onBriefRefresh()
        } catch (e) {
            setError(e.response?.data?.detail || 'Failed to send reply.')
        } finally {
            setSending(false)
        }
    }

    const startRerun = async () => {
        const msg = input.trim()
        if (!msg || sending) return
        setShowConfirm(false)
        setSending(true)
        setError('')
        try {
            await api.post(`/cases/${caseId}/reply`, { message: msg, mode: 'rerun' })
            setInput('')
            setRerunStatus('polling')
            let elapsed = 0
            pollRef.current = setInterval(async () => {
                elapsed += 10
                await loadThread()
                if (onBriefRefresh) await onBriefRefresh()
                if (elapsed >= 300) {
                    clearInterval(pollRef.current)
                    setRerunStatus(null)
                }
            }, 10000)
        } catch (e) {
            setError(e.response?.data?.detail || 'Failed to start re-research.')
        } finally {
            setSending(false)
        }
    }

    return (
        <div style={{
            marginTop: '24px',
            background: '#0f172a',
            border: '1px solid rgba(212,175,55,0.25)',
            borderRadius: '10px',
            overflow: 'hidden',
        }}>
            {/* Header */}
            <div style={{
                padding: '12px 16px',
                borderBottom: '1px solid rgba(255,255,255,0.07)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
                <span style={{ color: '#D4AF37', fontWeight: 'bold', fontSize: '14px' }}>
                    💬 Conversation Thread
                </span>
                {rerunStatus === 'polling' && (
                    <span style={{ fontSize: '12px', color: '#f59e0b' }}>
                        🔄 Re-research in progress...
                    </span>
                )}
            </div>

            {/* Thread */}
            <div style={{ padding: '16px', maxHeight: '400px', overflowY: 'auto' }}>
                {thread.length === 0 ? (
                    <p style={{ color: '#475569', fontSize: '13px', textAlign: 'center' }}>
                        No messages yet. Reply below to update the brief.
                    </p>
                ) : (
                    thread.map((msg) => <MessageBubble key={msg.id} msg={msg} />)
                )}
                <div ref={bottomRef} />
            </div>

            {/* Input area */}
            <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                {error && (
                    <div style={{ color: '#EF4444', fontSize: '12px', marginBottom: '8px' }}>{error}</div>
                )}
                <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) sendReply() }}
                    placeholder="Add new information, update constraints, or note what changed... (Ctrl+Enter to send)"
                    rows={3}
                    style={{
                        width: '100%', boxSizing: 'border-box',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px', padding: '10px',
                        color: '#ccd6f6', fontSize: '13px', resize: 'vertical',
                        fontFamily: 'inherit',
                    }}
                />
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <button
                        onClick={sendReply}
                        disabled={sending || !input.trim()}
                        style={{
                            background: sending ? '#1e3a5f' : '#1d4ed8',
                            color: 'white', border: 'none', borderRadius: '6px',
                            padding: '8px 20px', fontSize: '13px', fontWeight: 'bold',
                            cursor: sending || !input.trim() ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {sending ? '⏳ Updating...' : '✉️ Reply'}
                    </button>
                    <button
                        onClick={() => setShowConfirm(true)}
                        disabled={sending || !input.trim()}
                        style={{
                            background: 'transparent',
                            color: sending || !input.trim() ? '#475569' : '#D4AF37',
                            border: `1px solid ${sending || !input.trim() ? '#334155' : '#D4AF37'}`,
                            borderRadius: '6px', padding: '8px 20px',
                            fontSize: '13px', cursor: sending || !input.trim() ? 'not-allowed' : 'pointer',
                        }}
                    >
                        🔄 Re-research
                    </button>
                </div>
            </div>

            {/* Confirm modal for re-research */}
            {showConfirm && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                }}>
                    <div style={{
                        background: '#1e293b', border: '1px solid rgba(212,175,55,0.4)',
                        borderRadius: '12px', padding: '24px', maxWidth: '420px', width: '90%',
                    }}>
                        <h3 style={{ color: '#D4AF37', marginBottom: '12px' }}>🔄 Confirm Re-research</h3>
                        <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '16px', lineHeight: 1.5 }}>
                            This will re-run the full research debate. It takes 1–3 minutes.
                            The conversation context will be preserved as seed material.
                        </p>
                        <p style={{ color: '#ccd6f6', fontSize: '13px', marginBottom: '20px' }}>
                            <em>"{input.substring(0, 150)}{input.length > 150 ? '...' : ''}"</em>
                        </p>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowConfirm(false)}
                                style={{
                                    background: 'transparent', color: '#94a3b8',
                                    border: '1px solid #334155', borderRadius: '6px',
                                    padding: '8px 16px', cursor: 'pointer',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={startRerun}
                                style={{
                                    background: '#d97706', color: 'white', border: 'none',
                                    borderRadius: '6px', padding: '8px 20px',
                                    fontWeight: 'bold', cursor: 'pointer',
                                }}
                            >
                                Start Re-research
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
